import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Lock } from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { ShippingMethods } from '@/enums/shippingMethod';
import { en } from '@/intl/en';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getInvoiceById, getInvoiceSent, getIsInvoiceLoaded } from '@/redux/modules/checkout/checkout.selectors';
import { getCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getPaymentError, getPaymentErrorCode, getPaymentSubmitted, getPaymentSuccess } from '@/redux/modules/payment';
import { getFeeError } from '@/redux/modules/processingFee';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCatalogTermsUrl, getShippingPartnerPolicyUrl } from '@/utils/urls';

import { RowDivider } from '../RowDivider';
import { InvoiceSummaryList } from './components/InvoiceSummaryList';
import { InvoiceSummaryListRow } from './components/InvoiceSummaryListRow';

export type InvoiceSummaryPayNowProps = {
    amountToPay: number; //cents
    amountToPayZip: number; //dollars
    catalogId: number;
    currency: string;
    handleSubmitClick: () => void;
    readOnly: boolean;
    readyToPay: boolean;
    shippingMethod: ShippingMethods;
};

const CENTS_IN_A_DOLLAR = 100;
const NUMBER_OF_ZIP_PAYMENTS = 4;

export const InvoiceSummaryPayNow = ({
    amountToPay,
    amountToPayZip,
    catalogId,
    currency,
    handleSubmitClick,
    readOnly,
    readyToPay,
    shippingMethod,
}: InvoiceSummaryPayNowProps) => {
    const formatMessage = useAppFormatMessage();

    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const invoiceLoaded = useAppSelector((state) => getIsInvoiceLoaded(state, catalogId));
    const invoiceSent = useAppSelector((state) => getInvoiceSent(state, catalogId));
    const { invoice } = useAppSelector((state) => getInvoiceById(state, catalogId));
    const paymentSuccess = useAppSelector(getPaymentSuccess);
    const paymentSubmitted = useAppSelector(getPaymentSubmitted);
    // yes, it is possible that this might not be the key. But we account for that in the formatted message with a defaultMessage of the error message
    const paymentErrorCode = useAppSelector(getPaymentErrorCode) as keyof (typeof en)['checkout']['errorCodes'];
    const paymentError = useAppSelector(getPaymentError);
    const feeLoadingError = useAppSelector(getFeeError);
    const shippingQuote = useAppSelector(getCheckoutShippingQuote);

    const hasInfoText = Boolean(!readOnly || paymentErrorCode || paymentError || feeLoadingError);
    const getPolicies = () => {
        if (invoiceLoaded) {
            if (Number(shippingQuote?.total) > 0 && shippingMethod === ShippingMethods.LiveShipping) {
                return formatMessage(
                    ['checkout', 'balance', invoice.highRisk ? 'liveShippingHighRiskTerms' : 'liveShippingTerms'],
                    {
                        values: {
                            shippingPolicies: (
                                <Caption key="checkout-balance-live-shipping-partners-policies-link">
                                    <Link
                                        size="xs"
                                        target="_blank"
                                        href={getShippingPartnerPolicyUrl(shippingQuote.shippingProvider)}
                                    >
                                        {formatMessage(['checkout', 'balance', 'liveShippingPartnersPolicies'])}
                                    </Link>
                                </Caption>
                            ),
                            terms: (
                                <Caption key="checkout-balance-terms-of-sale-link">
                                    <Link
                                        size="xs"
                                        target="_blank"
                                        href={getCatalogTermsUrl(catalogId, catalogTitle)}
                                    >
                                        {formatMessage(['checkout', 'balance', 'termsOfSale'])}
                                    </Link>
                                </Caption>
                            ),
                        },
                    }
                );
            } else if (invoice.highRisk) {
                return formatMessage(['checkout', 'balance', 'highRiskTerms'], {
                    values: {
                        terms: (
                            <Caption key="checkout-balance-high-risk-terms-link">
                                <Link
                                    size="xs"
                                    target="_blank"
                                    href={getCatalogTermsUrl(catalogId, catalogTitle)}
                                >
                                    {formatMessage(['checkout', 'balance', 'termsOfSale'])}
                                </Link>
                            </Caption>
                        ),
                    },
                });
            }
        }
        return formatMessage(['checkout', 'balance', 'auctionHousePolicies'], {
            values: {
                terms: (
                    <Caption key="checkout-balance-auction-house-policies-link">
                        <Link
                            size="xs"
                            target="_blank"
                            href={getCatalogTermsUrl(catalogId, catalogTitle)}
                        >
                            {formatMessage(['checkout', 'balance', 'termsOfSale'])}
                        </Link>
                    </Caption>
                ),
            },
        });
    };

    const payNowClassName = 'w-full mt-xs mb-md';

    return (
        <InvoiceSummaryList>
            {invoiceLoaded ? (
                <div
                    className={cn('mt-2xs -mb-2xs', {
                        'mb-sm': hasInfoText,
                    })}
                >
                    <RowDivider />
                    {amountToPayZip ? (
                        <InvoiceSummaryListRow
                            amount={{
                                amount: paymentSuccess ? 0 : amountToPay / CENTS_IN_A_DOLLAR / NUMBER_OF_ZIP_PAYMENTS,
                                currency,
                            }}
                            bold
                            label={formatMessage(['checkout', 'balance', 'totalDueNow'])}
                        />
                    ) : (
                        <InvoiceSummaryListRow
                            amount={{
                                amount: paymentSuccess ? 0 : amountToPay / CENTS_IN_A_DOLLAR,
                                currency,
                            }}
                            bold
                            label={formatMessage(['checkout', 'balance', 'total'])}
                        />
                    )}
                </div>
            ) : (
                /* the &_div is because the Loading component has a div
                 * surrounding it that cannot have styles applied to it
                 */
                <div className="flex w-full [&_div]:mx-auto">
                    <Loading />
                </div>
            )}
            {amountToPay > 0 && invoiceSent && !paymentSuccess && (
                <div>
                    {amountToPayZip ? (
                        <Button
                            className={payNowClassName}
                            data-testid="zip-pay-now"
                            disabled={!readyToPay}
                            loading={paymentSubmitted}
                            onClick={handleSubmitClick}
                            size="sm"
                        >
                            {formatMessage(['checkout', 'balance', 'checkoutWith'])}
                            <img
                                alt="Zip"
                                className="mb-2xs ml-xs w-[50px]"
                                src="https://p1.liveauctioneers.com/dist/images/zip-logo-reverse.svg"
                            />
                        </Button>
                    ) : (
                        <Button
                            className={payNowClassName}
                            data-testid="pay-now"
                            disabled={!readyToPay}
                            loading={paymentSubmitted}
                            onClick={handleSubmitClick}
                        >
                            <>
                                <Lock
                                    className="inline-flex"
                                    color="inverse"
                                    size="sm"
                                />
                                {formatMessage(['checkout', 'balance', 'cta'])}
                            </>
                        </Button>
                    )}
                </div>
            )}
            {(Boolean(paymentErrorCode) || Boolean(paymentError) || Boolean(feeLoadingError)) && (
                <div className="mb-sm flex items-center">
                    {/* default message is the error code because it looks better than a failed translation. */}
                    <BodySecondary color="critical">
                        {formatMessage(['checkout', 'errorCodes', paymentErrorCode], {
                            defaultMessage: paymentErrorCode || paymentError || feeLoadingError,
                        })}
                    </BodySecondary>
                </div>
            )}
            {!readOnly && <Caption data-testid="checkout-policies">{getPolicies()}</Caption>}
        </InvoiceSummaryList>
    );
};
